// Bulk import for Browser profiles: the operator picks a format, drops in a
// creds file and then a list of proxies. This turns both into structured rows
// the worker's /admin/browser-profiles/bulk endpoint can store. Parsing lives
// here (not on the server) because "choose the format" is the UI's job and the
// preview table lets the operator eyeball the result before anything is saved.
import { normalizeTotpSecret } from './totp';

export interface CredsFormat {
  id: string;
  label: string;
  /** A short human description of the line layout shown under the picker. */
  hint: string;
}

// Add a format here and give it a branch in parseAccountLine.
export const CREDS_FORMATS: CredsFormat[] = [
  {
    id: 'login-pass-recovery-2fa-app',
    label: 'Login : password : recovery email : 2FA link : app password',
    hint: 'One account per line, fields split by “:”. An “Order ID: …” line above a block is kept as a note. The 2FA field may be a secret, an otpauth:// link or a 2fa.live link.',
  },
];

export interface ParsedAccount {
  line: number;
  order_id: string;
  login: string;
  password: string;
  recovery_email: string;
  /** Extracted base32 seed, or '' when the 2FA field could not be decoded. */
  totp_seed: string;
  /** The raw 2FA field as written, kept for the note when no seed was found. */
  totp_raw: string;
  app_password: string;
}

export interface ParseResult {
  accounts: ParsedAccount[];
  /** Lines that were neither an Order ID nor a usable account. */
  skipped: { line: number; text: string }[];
}

/**
 * Pulls a base32 TOTP seed out of whatever a seller wrote in the 2FA field:
 * a bare secret, an otpauth:// URI, or a link like https://2fa.live/tok/<secret>
 * whose last path segment is the secret. Returns '' when none can be read.
 */
export function extractTotpSeed(raw: string): string {
  const s = (raw ?? '').trim();
  if (!s) return '';
  const direct = normalizeTotpSecret(s);
  if (direct) return direct;
  if (s.includes('/')) {
    const seg = s.split(/[/?#]/).filter(Boolean).pop() ?? '';
    const viaSeg = normalizeTotpSecret(seg);
    if (viaSeg) return viaSeg;
  }
  return '';
}

// login:password:recoveryEmail:2faLink:appPassword — but the 2FA link is a URL
// full of colons, so we never split the whole line. The first three fields are
// taken from the left (login, password, recovery), and a trailing app password
// is split off only when the last ":"-token looks like a Google app password
// (16 ascii letters); everything in between is the 2FA field, URL and all.
function parseAccountLine(line: string): Omit<ParsedAccount, 'line' | 'order_id'> | null {
  const i1 = line.indexOf(':');
  if (i1 < 0) return null;
  const login = line.slice(0, i1).trim();
  // A real account line starts with an email. This also skips stray headers.
  if (!login.includes('@')) return null;

  const i2 = line.indexOf(':', i1 + 1);
  if (i2 < 0) return { login, password: line.slice(i1 + 1).trim(), recovery_email: '', totp_seed: '', totp_raw: '', app_password: '' };
  const password = line.slice(i1 + 1, i2);

  const i3 = line.indexOf(':', i2 + 1);
  if (i3 < 0) return { login, password, recovery_email: line.slice(i2 + 1).trim(), totp_seed: '', totp_raw: '', app_password: '' };
  const recovery_email = line.slice(i2 + 1, i3).trim();

  let rest = line.slice(i3 + 1);
  let app_password = '';
  const tail = rest.lastIndexOf(':');
  if (tail >= 0 && /^[a-z]{16}$/.test(rest.slice(tail + 1))) {
    app_password = rest.slice(tail + 1);
    rest = rest.slice(0, tail);
  }
  const totp_raw = rest.trim();
  return { login, password, recovery_email, totp_seed: extractTotpSeed(totp_raw), totp_raw, app_password };
}

const ORDER_RE = /^order\s*id\s*[:#-]?\s*(.+)$/i;

export function parseCreds(formatId: string, text: string): ParseResult {
  const accounts: ParsedAccount[] = [];
  const skipped: { line: number; text: string }[] = [];
  let orderId = '';
  const lines = text.split(/\r?\n/);
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i]!.trim();
    if (!raw) continue;
    const om = ORDER_RE.exec(raw);
    if (om) { orderId = om[1]!.trim(); continue; }
    // Only one format for now; the picker guards the id.
    const acc = formatId === 'login-pass-recovery-2fa-app' ? parseAccountLine(raw) : null;
    if (acc) accounts.push({ ...acc, line: i + 1, order_id: orderId });
    else skipped.push({ line: i + 1, text: raw });
  }
  return { accounts, skipped };
}

/** One proxy per line, blanks and #-comments dropped, kept in the file's order. */
export function parseProxies(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l && !l.startsWith('#'));
}

/** A readable profile name from the login: the part before @, else the whole login. */
export function profileNameFromLogin(login: string): string {
  const local = login.split('@')[0]!.trim();
  return (local || login).slice(0, 120);
}
